import moment from 'moment-timezone';
import {
  formatCompactTime,
  formatSummaryDate,
  getDeviceTimezone,
} from '@/utils/date';

export interface DateTimeSelection {
  date: Date;
  time?: Date;
}

/**
 * Formats a `Date` into a 24-hour `HH:mm` string for `DatePickerCalendar`'s
 * time slot (e.g. `"09:05"`, `"14:30"`).
 *
 * @param {Date} date - The date whose local hours/minutes are formatted.
 *
 * @returns The zero-padded 24-hour time string, `HH:mm`.
 */
export const formatTimePickerString = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${hours}:${minutes}`;
};

/**
 * Formats a `Date` and a 24-hour `HH:mm` string from `DatePickerCalendar`'s
 * time slot (e.g. `"09:05"`, `"14:30"`) into a Date representing the selected time.
 *
 * @param {Date} date - The date whose local hours/minutes are formatted.
 * @param {string} timeString - a 24-hour `HH:mm` string from `DatePickerCalendar`'s
 * time slot (e.g. `"09:05"`, `"14:30"`).
 *
 * @returns {Date} A Date representing a selected date + time.
 */
export const formatTimePickerDate = (date: Date, timeString: string): Date => {
  const [hours, minutes] = timeString.split(':').map(Number);

  const time = new Date(date);
  time.setHours(hours, minutes, 0, 0);

  return time;
};

/**
 * Returns the next selectable time slot strictly after "now" as a `Date`,
 * rounded up to the next `minuteSteps` boundary.
 *
 * The slot is always in the future, so seeding a picker with it never
 * produces a selection that is already past. Rolling past midnight moves the
 * slot onto the next day, so callers must read the date from the result too.
 *
 * @param {number} minuteSteps - The minute interval to round up to (e.g. `15`).
 *
 * @returns {Date} The next valid time slot with seconds and milliseconds cleared.
 */
export const getNextValidTimeSlot = (minuteSteps: number): Date => {
  const now = new Date();
  const nextSlot = new Date(now);
  nextSlot.setMinutes(
    Math.floor(now.getMinutes() / minuteSteps) * minuteSteps + minuteSteps,
    0,
    0,
  );

  return nextSlot;
};

export const startOfDay = (date: Date): Date => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);

  return day;
};

export const TIME_PICKER_MINUTE_STEPS = 15;

export const isToday = (date: Date): boolean => {
  const today = new Date();

  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};

export const formatDateTimeSelection = ({
  date,
  time,
}: DateTimeSelection): string => {
  const dateLabel = formatSummaryDate(
    moment(date).tz(getDeviceTimezone(), true),
  );

  return time ? `${dateLabel} @${formatCompactTime(time)}` : dateLabel;
};
